import { CUSTOMER_GET, CUSTOMER_UPDATE } from '@constants';
import {
  CustomerForm,
  toCustomerPayload,
  fromApiCustomer,
} from '@forms/CustomerForm';
import { CustomerFormValues } from '@forms/CustomerForm/types';
import { useAuthHttp } from '@hooks';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDocumentTitle } from 'usehooks-ts';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';

export function EditCustomer() {
  useDocumentTitle('FLOX - Edit Customer');
  const authHttp = useAuthHttp();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [defaultValues, setDefaultValues] =
    useState<Partial<CustomerFormValues>>();
  // The read-model record carries MDM fields the form does not edit (hierarchy,
  // market, partners, etc.); keep it so the update payload preserves them
  // instead of overwriting with defaults.
  const [originalCustomer, setOriginalCustomer] =
    useState<Record<string, unknown>>();

  const pages = [
    { name: 'Home', href: '/', current: false },
    { name: 'Customer Master', href: '/customer-master', current: false },
    {
      name: 'Edit Customer',
      href: `/customer-master/edit/${id}`,
      current: true,
    },
  ];

  useEffect(() => {
    if (!id) return undefined;
    let isStillOnThisCustomer = true;

    const fetchCustomer = async () => {
      setIsLoading(true);
      setOriginalCustomer(undefined);
      setDefaultValues(undefined);
      try {
        const { data } = await authHttp.post(CUSTOMER_GET, {
          id,
          isIncludingArchived: true,
        });

        const customer = data?.customers?.[0];
        if (customer && isStillOnThisCustomer) {
          setOriginalCustomer(customer);
          setDefaultValues({
            customerId: customer.customerId || '',
            name: customer.name || '',
            type: customer.type || '',
            contactName: customer.contactName || '',
            contactEmail: customer.contactEmail || '',
            contactNumber: customer.contactNumber || '',
            hqId: customer.hqId || '',
            hqName: customer.hqName || '',
            address: customer.address || '',
            country: customer.country || '',
            postcode: customer.postcode || '',
            location: {
              lat: customer.location?.lat || 0,
              lon: customer.location?.lon || 0,
            },
            // TimeOnly serialises as "HH:mm:ss" — Timepicker wants "HH:mm"
            operatingHoursFrom:
              customer.operatingHoursFrom?.slice(0, 5) || '08:30',
            operatingHoursTo: customer.operatingHoursTo?.slice(0, 5) || '19:00',
            notes: customer.notes || '',
            ...fromApiCustomer(customer),
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        if (isStillOnThisCustomer) setIsLoading(false);
      }
    };

    fetchCustomer();
    return () => {
      isStillOnThisCustomer = false;
    };
  }, [id]);

  const handleOnSubmit = async (data: CustomerFormValues) => {
    setIsSubmitting(true);
    try {
      const { data: updateCustomerData } = await authHttp.post(
        CUSTOMER_UPDATE,
        {
          customer: {
            ...originalCustomer,
            ...toCustomerPayload(data),
            id,
          },
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (updateCustomerData) {
        navigate('/customer-master');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-[calc(100vh-12rem)]'>
        <div className='flex flex-col items-center gap-3'>
          <FloxSpinner size='md' />
          <p className='text-gray-500 text-sm'>Loading customer...</p>
        </div>
      </div>
    );
  }

  if (!defaultValues) {
    return (
      <div className='flex items-center justify-center h-[calc(100vh-12rem)]'>
        <p className='text-gray-500'>Customer not found</p>
      </div>
    );
  }

  return (
    <CustomerForm
      onSubmit={handleOnSubmit}
      isSubmitting={isSubmitting}
      header='Edit Customer'
      pages={pages}
      defaultValues={defaultValues}
    />
  );
}
